import { useCallback, useEffect, useRef, useState } from "react";

// Highchart
import Highcharts from "highcharts";
import HC_more from "highcharts/highcharts-more";

import "assets/styles/components/highcharts.css";

// Config
import { getOptions } from "./BehaviouralBreakdown.config";

// Hooks
import useChartSlice from "store/slice/charts/useChartSlice";

// Data
import { convertData, carouselData, carouselDataTitles } from "./BehaviouralBreakdown.data";

// Types
import { LD12Chart } from "store/slice/charts/chartsSlice.types";
import { ChartStatus, TBehaviourLabels } from "./BehaviouralBreakdown.types";
import { IBaseChartProps, IRefObjectForHighchartsReact } from "components/Charts/chart.types";

// Utils
import { COLORS } from "constants/chartCommons";
import { flattenDeep, isEmpty, isEqual } from "lodash";
import { getElementBBox } from "utils";

// Create chart elements
import {
    createMasks,
    createWrapperGroups,
    createSubBehaviourBackground,
    createBehaviourBar,
    createExpanderButton,
    createPlotLines,
    createLabels,
    createSubLabels,
    createSummaryDot,
    createSummaryDotLabel,
    createDotLineConnector,
    createSubBehaviourDots,
    createSubDotLineConnector,
    buttonHeight,
    chartVerticalSpace,
    gapBetweenBehaviours,
    legendSpace,
    rowHeight,
    transitionSpeed,
    hubAverageColor,
} from "./BehaviouralBreakdown.functions";
import { TCustomiseLabelsOptions } from "../CustomiseLabels/CustomiseLabels.config";

HC_more(Highcharts);

const useBehaviouralBreakdown = ({ width, height, moreOptions, getData }: IBaseChartProps) => {
    const paceCurrentSlide = useRef(0);
    const triadCurrentSlide = useRef(0);

    const chartData = useChartSlice(getData) as LD12Chart;
    const prevChartData = useRef<LD12Chart | undefined>(undefined);

    const chartRef = useRef<IRefObjectForHighchartsReact>(null);
    const dataRef = useRef<ReturnType<typeof convertData> | undefined>(undefined);

    const [chartOptions, setChartOptions] = useState<Highcharts.Options | null>(null);
    const [chartLegendHeight, setChartLegendHeight] = useState(0);

    const [customiseLabels, setCustomiseLabels] = useState<TCustomiseLabelsOptions>({
        "show-names": false,
    });

    const [shouldExpandAll, setShouldExpandAll] = useState(false);
    const [showDefinitions, setShowDefinitions] = useState(false);
    const [filterBy, setFilterBy] = useState<TBehaviourLabels | undefined>();

    const [render, setRender] = useState(false);

    let data = {} as ReturnType<typeof convertData>;
    const shouldShowDefinitions = moreOptions?.shouldShowPaceDefinitions || showDefinitions;

    const handleCheckExpandAll = (ev: React.ChangeEvent<HTMLInputElement>) => {
        handleExpandAll(ev.target.checked);
        if (!ev.target.checked && moreOptions?.setShouldShowPaceDefinitions) {
            moreOptions.setShouldShowPaceDefinitions(ev.target.checked);
        }
    };

    const handleCheckPaceDefinitions = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = ev.target.checked;

        setShowDefinitions(isChecked);

        chartRef.current?.chart.update({
            exporting: {
                enabled: !isChecked,
            },
        });

        if (!isChecked && moreOptions?.setShouldShowPaceDefinitions) {
            moreOptions.setShouldShowPaceDefinitions(isChecked);
        }
    };

    const handleSetCustomiseLabels = (options: TCustomiseLabelsOptions) => {
        setCustomiseLabels(options);
    };

    const checkExpandedItems = () => {
        if (isEmpty(data) && !isEmpty(dataRef.current)) {
            data = dataRef.current as ReturnType<typeof convertData>;
        }

        setShouldExpandAll(data.filter((d) => !isEmpty(d.svgs.button.svg)).every((e) => e.expanded));
    };

    const hideDotAndLine = (keyIndex: number, transitionSpeed: number) => {
        const currentEl = data[keyIndex];
        const previousEl = data[keyIndex - 1];
        const nextEl = data[keyIndex + 1];

        if (currentEl.expanded) {
            // Dots
            currentEl.svgs.dots.forEach((dot) => {
                dot.config = {
                    ...dot.config,
                    css: {
                        ...dot.config?.css,
                        fill: "transparent",
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                dot.svg.css(dot.config.css as Highcharts.CSSObject);
            });

            // Dot Label
            currentEl.svgs.dotsLabels.forEach((dotLabel) => {
                dotLabel.config = {
                    ...dotLabel.config,
                    css: {
                        ...dotLabel.config.css,
                        color: "transparent",
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                dotLabel.svg.css(dotLabel.config.css as Highcharts.CSSObject);
            });

            // Lines
            currentEl.svgs?.lines?.length &&
                currentEl.svgs.lines.forEach((line) => {
                    line.config = {
                        ...line.config,
                        css: {
                            ...line.config?.css,
                            "stroke-width": 0,
                            transition: `all ${transitionSpeed / 2}ms`,
                        },
                    };
                    line.svg.css(line.config.css as Highcharts.CSSObject);
                });
            previousEl?.svgs?.lines?.length &&
                previousEl.svgs.lines.forEach((line) => {
                    line.config = {
                        ...line.config,
                        css: {
                            ...line.config?.css,
                            "stroke-width": 0,
                            transition: `all ${transitionSpeed / 2}ms`,
                        },
                    };
                    line.svg.css(line.config.css as Highcharts.CSSObject);
                });
        } else {
            setTimeout(() => {
                // Dots
                currentEl.svgs.dots.forEach((dot, i) => {
                    const isHubAverage = currentEl.data[i].label === "Hub Average";
                    const fillColor = isHubAverage ? hubAverageColor : COLORS[i];
                    dot.config = {
                        ...dot.config,
                        css: {
                            ...dot.config.css,
                            fill: data[keyIndex].isVisible[i] ? fillColor : "transparent",
                            transition: `all ${transitionSpeed / 2}ms`,
                        },
                    };
                    dot.svg.css(dot.config.css as Highcharts.CSSObject);
                });

                // Dot Label
                currentEl.svgs.dotsLabels.forEach((dotLabel, i) => {
                    dotLabel.config = {
                        ...dotLabel.config,
                        css: {
                            ...dotLabel.config.css,
                            color: "#454852",
                            opacity: data[keyIndex].isVisible[i] ? 1 : 0,
                            transition: `all ${transitionSpeed / 2}ms`,
                        },
                    };
                    dotLabel.svg.css(dotLabel.config.css as Highcharts.CSSObject);
                });

                // Lines
                nextEl &&
                    !nextEl.expanded &&
                    currentEl.svgs.lines.forEach((line, i) => {
                        line.config = {
                            ...line.config,
                            css: {
                                ...line.config.css,
                                "stroke-width": data[keyIndex].isVisible[i] ? 1 : 0,
                                transition: `all ${transitionSpeed / 2}ms`,
                            },
                        };
                        line.svg.css(line.config.css as Highcharts.CSSObject);
                    });
                previousEl?.svgs?.lines?.length &&
                    !previousEl.expanded &&
                    previousEl.svgs.lines.forEach((line, i) => {
                        line.config = {
                            ...line.config,
                            css: {
                                ...line.config.css,
                                "stroke-width": data[keyIndex].isVisible[i] ? 1 : 0,
                                transition: `all ${transitionSpeed / 2}ms`,
                            },
                        };
                        line.svg.css(line.config.css as Highcharts.CSSObject);
                    });
            }, transitionSpeed / 4);
        }
    };

    const handleExpandCollapse = (i: number, chart: Highcharts.Chart, transitionSpeed: number) => {
        data[i].expanded = !data[i].expanded;

        const height = data[i].subData?.data?.length || 1;

        data[i].svgs.mask.config = {
            ...data[i].svgs.mask.config,
            css: {
                ...data[i].svgs.mask.config.css,
                height: `${!data[i].expanded ? rowHeight + buttonHeight : height * rowHeight + buttonHeight}px`,
                transition: `all ${transitionSpeed}ms`,
            },
        };
        data[i].svgs.mask.svg.css(data[i].svgs.mask.config.css || {});

        data[i].svgs.button.config = {
            ...data[i].svgs.button.config,
            css: {
                ...data[i].svgs.button.config.css,
                transform: `translate(${chartVerticalSpace}px, ${
                    !data[i].expanded ? `${rowHeight + 2}px` : `${height * rowHeight}px`
                })`,
                transition: `all ${transitionSpeed}ms`,
            },
        };
        data[i].svgs.button.svg.css(data[i].svgs.button.config.css as Highcharts.CSSObject);

        data[i].svgs.buttonChevrons.forEach((chevron) => {
            const chevronWidth = chevron.svg.element.getBoundingClientRect().width;
            chevron.config = {
                ...chevron.config,
                css: {
                    ...chevron.config?.css,
                    transform: `translate(${
                        // @ts-ignore
                        chevron.svg.element.getCTM().e + (!data[i].expanded ? -chevronWidth - 2 : chevronWidth + 2)
                    }px, ${!data[i].expanded ? `${rowHeight + 1}px` : `${height * rowHeight + 10}px`}) rotate(${
                        !data[i].expanded ? 0 : 180
                    }deg)`,
                    transition: `all ${transitionSpeed}ms`,
                },
            };
            chevron.svg.css(chevron.config.css as Highcharts.CSSObject);
        });

        data[i].svgs.subData?.lines?.length &&
            data[i].svgs.subData.lines.forEach((arrayOfLines) => {
                arrayOfLines.forEach((line, lineIdx) => {
                    const isVisible = data[i].isVisible[lineIdx];
                    const isEnabled = data[i].data[lineIdx].enabled;
                    const isExpanded = data[i].expanded;

                    const isHubAverage = data[i].data[lineIdx].label === "Hub Average";
                    const strokeWidth = isVisible && isExpanded ? 1 : 0;

                    line.config = {
                        ...line.config,
                        attr: {
                            ...(line.config.attr as Highcharts.SVGAttributes),
                            "stroke-width": !isEnabled ? 0 : strokeWidth,
                            stroke: isHubAverage ? hubAverageColor : COLORS[lineIdx],
                        },
                        css: {
                            ...line.config?.css,
                            transition: `all ${transitionSpeed / 2}ms`,
                        },
                    };
                    line.svg.attr(line.config.attr);
                    line.svg.css(line.config.css as Highcharts.CSSObject);
                });
            });

        data[i].svgs.subData?.dots?.length &&
            data[i].svgs.subData.dots.forEach((arrayOfDots) => {
                arrayOfDots.forEach((dot, dotIdx) => {
                    const isVisible = data[i].isVisible[dotIdx] ? 1 : 0;
                    const isEnabled = data[i].data[dotIdx].enabled;
                    const isExpanded = data[i].expanded;

                    const isHubAverage = data[i].data[dotIdx].label === "Hub Average";
                    const fillColor = isHubAverage ? hubAverageColor : COLORS[dotIdx];
                    const fillOpacity = isVisible && isExpanded ? 1 : 0;

                    dot.config = {
                        ...dot.config,
                        css: {
                            ...dot.config.css,
                            fill: !isEnabled ? "transparent" : fillColor,
                            opacity: !isEnabled ? 0 : fillOpacity,
                            transition: `all ${transitionSpeed / 2}ms`,
                        },
                    };
                    dot.svg.css(dot.config.css as Highcharts.CSSObject);
                });
            });

        data[i].svgs.subData.dotsLabels?.length &&
            data[i].svgs.subData.dotsLabels.forEach((label, idx) => {
                const isVisible = data[i].isVisible[idx];
                const isEnabled = data[i].data[idx].enabled;
                const isExpanded = data[i].expanded;
                const color = isVisible && isExpanded ? "#454852" : "transparent";

                label.config = {
                    ...label.config,
                    css: {
                        ...label.config.css,
                        color: isEnabled ? "#454852" : color,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                label.svg.css(label.config.css as Highcharts.CSSObject);
            });

        data[i].svgs.labels?.length &&
            data[i].svgs.labels.forEach((label) => {
                label.config = {
                    ...label.config,
                    css: {
                        ...label.config.css,
                        opacity: !data[i].expanded ? 1 : 0,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                label.svg.css(label.config.css as Highcharts.CSSObject);
            });

        data[i].svgs.subData.labels?.length &&
            data[i].svgs.subData.labels.forEach((labelGroup) => {
                labelGroup.forEach((label) => {
                    label.config = {
                        ...label.config,
                        css: {
                            ...label.config.css,
                            opacity: !data[i].expanded ? 0 : 1,
                            transition: `all ${transitionSpeed / 2}ms`,
                        },
                    };
                    label.svg.css(label.config.css as Highcharts.CSSObject);
                });
            });

        data[i].svgs.rotatedLabels?.length &&
            data[i].svgs.rotatedLabels.forEach((label) => {
                label.config = {
                    ...label.config,
                    css: {
                        ...label.config.css,
                        opacity: !data[i].expanded ? 0 : 1,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                label.svg.css(label.config.css as Highcharts.CSSObject);
            });

        hideDotAndLine(i, transitionSpeed);

        if (i < data.length - 1) {
            data.slice(i + 1, data.length).forEach((element) => {
                // @ts-ignore
                const heightToPush = element.svgs.wrapperGroup.svg.element.getCTM().f;

                element.svgs.wrapperGroup.config = {
                    ...element.svgs.wrapperGroup.config,
                    css: {
                        ...element.svgs.wrapperGroup.config.css,
                        transform: `translate(0, ${
                            !data[i].expanded
                                ? `${heightToPush - (data[i].subData?.data?.length - 1 || 1) * rowHeight}px`
                                : `${heightToPush + (data[i].subData?.data?.length - 1 || 1) * rowHeight}px`
                        })`,
                        transition: `all ${transitionSpeed}ms`,
                    },
                };

                element.svgs.wrapperGroup.svg.css(element.svgs.wrapperGroup.config.css || {});
            });
        }

        dataRef.current = data;
        handleChartSize(chart);
        checkExpandedItems();
    };

    const handleUpdateCarousel = (label: string, parentLabel: string) => {
        if (carouselDataTitles.map((e) => e.name).includes(parentLabel.toLowerCase() as TBehaviourLabels)) {
            const slides = carouselData.filter((el) => el.parent.toLowerCase() === parentLabel.toLowerCase());
            paceCurrentSlide.current = slides.findIndex(({ title }) =>
                label.toLowerCase().replace("-", " ").includes(title.toLowerCase().replace("-", " "))
            );
            setFilterBy(parentLabel.toLowerCase() as TBehaviourLabels);
        } else {
            const slides = carouselData.filter(
                (el) => !carouselDataTitles.map((e) => e.name).includes(el.parent.toLowerCase() as TBehaviourLabels)
            );
            triadCurrentSlide.current = slides.findIndex(({ title }) =>
                label.toLowerCase().replace("-", " ").includes(title.toLowerCase().replace("-", " "))
            );
        }

        setRender((val) => !val);
    };

    const handleExpandAll = (isChecked: boolean) => {
        if (isEmpty(data) && !isEmpty(dataRef.current)) {
            data = dataRef.current as ReturnType<typeof convertData>;
        }
        data.filter((d) => !isEmpty(d.svgs.button.svg)).forEach((el, i) => {
            if (isChecked) {
                !el.expanded &&
                    handleExpandCollapse(
                        i,
                        (chartRef.current as IRefObjectForHighchartsReact).chart as Highcharts.Chart,
                        0
                    );
            } else {
                el.expanded &&
                    handleExpandCollapse(
                        i,
                        (chartRef.current as IRefObjectForHighchartsReact).chart as Highcharts.Chart,
                        0
                    );
            }
        });

        if (!isEmpty(data)) {
            dataRef.current = data;
        }

        setShouldExpandAll(isChecked);
    };

    const handleLegendItemClick = useCallback((serie: Highcharts.Series) => {
        if (!dataRef.current) {
            return;
        }

        const index = serie.index;

        dataRef.current.forEach((el, i) => {
            if (!dataRef.current) {
                return;
            }

            el.isVisible[index] = !el.isVisible[index];

            const isVisible = el.isVisible[index];
            const isEnabled = el.data[index].enabled;
            const isExpanded = dataRef.current[i].expanded;
            const isHubAverage = el.data[index]?.label === "Hub Average";

            const isNotExpandedAndVisible = !isExpanded && isVisible;

            const baseColor = isHubAverage ? hubAverageColor : COLORS[index];
            const fillColor = isNotExpandedAndVisible ? baseColor : "transparent";
            const visibility = isVisible ? 1 : 0;

            // Summary Dots
            el.svgs.dots[index].config = {
                ...el.svgs.dots[index].config,
                css: {
                    ...el.svgs.dots[index].config.css,
                    opacity: !isEnabled ? 0 : visibility,
                    fill: !isEnabled ? "transparent" : fillColor,
                    transition: `all ${transitionSpeed / 2}ms`,
                },
            };
            el.svgs.dots[index].svg.css(el.svgs.dots[index].config.css as Highcharts.CSSObject);

            // Summary Dot line connector
            const strokeWidthLines = isNotExpandedAndVisible ? 1 : 0;

            if (el.svgs.lines[index]) {
                el.svgs.lines[index].config = {
                    ...el.svgs.lines[index].config,
                    css: {
                        ...el.svgs.lines[index].config?.css,
                        opacity: !isEnabled ? 0 : visibility,
                        "stroke-width": !isEnabled ? 0 : strokeWidthLines,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                el.svgs.lines[index].svg.css(el.svgs.lines[index].config.css as Highcharts.CSSObject);
            }

            // Summary Dot labels
            if (i === 0 || i === 4) {
                el.svgs.dotsLabels[index].config = {
                    ...el.svgs.dotsLabels[index].config,
                    css: {
                        ...el.svgs.dotsLabels[index].config.css,
                        opacity: !isEnabled ? 0 : visibility,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                el.svgs.dotsLabels[index].svg.css(el.svgs.dotsLabels[index].config.css as Highcharts.CSSObject);
            }

            // Subdata Dot
            el.svgs.subData.dots.forEach((subEl, i) => {
                subEl[index].config = {
                    ...subEl[index].config,
                    css: {
                        ...subEl[index].config.css,
                        opacity: !isEnabled ? 0 : visibility,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                subEl[index].svg.css(subEl[index].config.css as Highcharts.CSSObject);
            });

            // Subdata Dot label
            if (!isEmpty(el.svgs.subData.dotsLabels[index])) {
                el.svgs.subData.dotsLabels[index].config = {
                    ...el.svgs.subData.dotsLabels[index].config,
                    css: {
                        ...el.svgs.subData.dotsLabels[index].config.css,
                        opacity: !isEnabled ? 0 : visibility,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                el.svgs.subData.dotsLabels[index].svg.css(
                    el.svgs.subData.dotsLabels[index].config.css as Highcharts.CSSObject
                );
            }

            // Subdata Dot line connector
            const strokeWidthSublines = isVisible && isExpanded ? 1 : 0;

            el.svgs.subData.lines.forEach((subEl, i) => {
                subEl[index].config = {
                    ...subEl[index].config,
                    attr: {
                        "stroke-width": !isEnabled ? 0 : strokeWidthSublines,
                    },
                    css: {
                        ...subEl[index].config.css,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                subEl[index].svg.css(subEl[index].config.css as Highcharts.CSSObject);
            });
        });
    }, []);

    const handleChartSize = useCallback((chart: Highcharts.Chart) => {
        if (!dataRef.current) {
            return;
        }

        const legendHeight = chart.legend.box.getBBox().height || 0;

        // Because we lose data on each render, use dataRef instead
        const expandedHeight =
            dataRef.current.reduce((acc, item) => {
                const subDataLength = item.subData.data.length;
                acc +=
                    rowHeight +
                    (subDataLength ? buttonHeight : 0) +
                    (item.expanded ? (item.subData.data.length - 1) * rowHeight : 0) +
                    gapBetweenBehaviours;
                return acc;
            }, 0) +
            legendSpace +
            legendHeight;

        chart.setSize(null, expandedHeight, true);
    }, []);

    const handleDrawChart = useCallback(
        (chart: Highcharts.Chart, isExporting: boolean, isRedrawing: boolean) => {
            const noData = isEmpty(data) && isEmpty(dataRef.current);
            const status: ChartStatus = {
                isFirstRender: !isExporting && !isRedrawing,
                isRedrawing,
                isExporting,
            };

            if (!chartData.series_labels.length || (isRedrawing && noData)) {
                return;
            }

            // Convert chartData on load, then on every redraw we will use the data in dataRef.current
            if (status.isFirstRender) {
                data = convertData(chartData);
            }

            // We lose data on every render, so use the copy we had in dataRef
            if ((isEmpty(data) && dataRef.current) || (dataRef.current && !isEqual(data, dataRef.current))) {
                data = dataRef.current;
            }

            const maximumWidth = chart.plotWidth - chartVerticalSpace * 2;
            const chartMaxWidth = maximumWidth;
            const r = chart.renderer;
            const dataEntries = Object.entries(data);

            const previousGroup: Highcharts.SVGElement | undefined = undefined;
            let barElement: ReturnType<typeof getElementBBox> | undefined = undefined;

            // Create Masks
            createMasks(dataEntries, data, status, chart, r);

            // Create Groups
            createWrapperGroups(dataEntries, data, status, r, previousGroup);

            // Create Bars, Dots and Subdata
            dataEntries.forEach((_, i) => {
                if (status.isExporting) {
                    data[i].svgs.group.svgExport.clip(data[i].svgs.mask.svgExport);
                } else {
                    if (!isEmpty(data[i].svgs.group.svg)) {
                        data[i].svgs.group.svg.clip(data[i].svgs.mask.svg);
                    }
                }

                // BACKGROUND PER SUB-BEHAVIOUR
                createSubBehaviourBackground(data[i], status, r, chart, chartMaxWidth);

                // BAR
                createBehaviourBar(data[i], status, r, chart, chartMaxWidth);
                barElement = isExporting
                    ? getElementBBox(data[i].svgs.bar.svgExport.element)
                    : getElementBBox(data[i].svgs.bar.svg.element);

                // BUTTON
                if (data[i].subData?.data.length) {
                    createExpanderButton(data[i], status, r, chart, chartMaxWidth, shouldShowDefinitions, () =>
                        handleExpandCollapse(i, chart, transitionSpeed)
                    );
                }

                // LINES
                createPlotLines(
                    data[i],
                    status,
                    r,
                    chart,
                    chartMaxWidth,
                    status.isExporting ? data[i].svgs.group.svgExport : data[i].svgs.group.svg
                );

                // LABELS
                createLabels(
                    data[i],
                    status,
                    r,
                    chart,
                    chartMaxWidth,
                    shouldShowDefinitions,
                    barElement,
                    (label, parent) => handleUpdateCarousel(label, parent)
                );

                // SUB-LABELS
                createSubLabels(data[i], status, r, chartMaxWidth, shouldShowDefinitions, (label, parent) =>
                    handleUpdateCarousel(label, parent)
                );
            });

            // ----------------------------------------------------------------
            // SERIES
            // ----------------------------------------------------------------

            // SUMMARY DOT
            createSummaryDot(dataEntries, status, r, barElement);

            // SUMMARY DOT LABELSol
            createSummaryDotLabel(dataEntries, status, r, customiseLabels);

            // LINES THAT CONNECT DOTS
            createDotLineConnector(dataEntries, status, r);

            // SUB-BEHAVIOUR DOTS
            createSubBehaviourDots(dataEntries, status, r, barElement, customiseLabels);

            // LINES THAT CONNECT SUBDOTS
            createSubDotLineConnector(dataEntries, status, r);

            setRender((val) => !val);

            prevChartData.current = chartData;
            dataRef.current = data;
        },
        [customiseLabels, shouldShowDefinitions, chartData]
    );

    // Update chart data when chartData changes
    // This will destroy all SVGs and re-create the chart again.
    useEffect(() => {
        if (!chartRef.current?.chart) return;

        if (prevChartData.current && !isEqual(chartData, prevChartData.current)) {
            const items = dataRef.current?.map((item) => Object.values(item.svgs));
            const allSvgSetups = items?.flatMap((item) =>
                item.flatMap((it, i, { length }) => {
                    if (length - 1 === i) {
                        return flattenDeep(Object.values(it));
                    } else {
                        return it;
                    }
                })
            );
            allSvgSetups?.forEach((setup) => {
                if (setup?.svg && !isEmpty(setup.svg)) setup.svg.destroy();
            });

            dataRef.current = undefined;
            handleDrawChart(chartRef.current.chart, false, false);
        }

        const options = getOptions({
            chartData,
            chartRef: chartRef.current.chart,
            handleDrawChart,
            handleLegendItemClick,
        });
        setChartOptions(options);
    }, [chartData, handleDrawChart, handleLegendItemClick]);

    // Update chart legend height after data change
    // This way highcharts adjust the chart height
    useEffect(() => {
        if (!chartRef.current?.chart) return;

        const legendHeight = chartRef.current.chart.legend.box.getBBox().height;

        if (chartLegendHeight !== legendHeight) {
            setChartLegendHeight(legendHeight);
            handleChartSize(chartRef.current.chart);
        }
    }, [chartLegendHeight, handleChartSize, render]);

    // Init
    useEffect(() => {
        if (isEmpty(chartData)) return;

        const options = getOptions({ chartData, handleDrawChart, handleLegendItemClick });
        setChartOptions(options);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        chartOptions,
        chartData,
        chartRef,
        shouldExpandAll,
        showDefinitions,
        paceCurrentSlide,
        triadCurrentSlide,
        filterBy,
        customiseLabels,
        setFilterBy,
        handleExpandAll,
        handleCheckExpandAll,
        handleCheckPaceDefinitions,
        handleSetCustomiseLabels,
    };
};

export default useBehaviouralBreakdown;
